import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useRef
} from 'react';
import { LinearProgress, Typography, Grid, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  validatePassword,
  getPasswordStrengthLevel,
  strengthColors,
  STRENGTH_LABELS
} from '../../Utils/Password/validatePassword';
import TextInput from '../Inputs/TextInput';
import ShowPassword from '../Adornments/ShowPassword';
import clsx from 'clsx';

const normalizeText = text => {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

const PasswordInputStrengthMeter = ({
  password,
  currentPassword,
  username,
  businessName,
  clientType,
  inputRef,
  label,
  error,
  helperText,
  name,
  onChange,
  required = true,
  withInput = false,
  onErrorsChange
}) => {
  const classes = useStyles();
  const [passwordShown, setPasswordShown] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const passwordInputRef = useRef(null);

  const validationResult = useMemo(() => {
    let processedUsername = '';
    if (clientType === 'natural-person' && username) {
      if (typeof username === 'object') {
        processedUsername = `${normalizeText(
          username.firstName || ''
        )} ${normalizeText(username.lastName || '')}`.trim();
      } else if (typeof username === 'string') {
        processedUsername = normalizeText(username || '');
      }
    } else if (!clientType && username) {
      if (typeof username === 'object') {
        processedUsername = `${normalizeText(
          username.firstName || ''
        )} ${normalizeText(username.lastName || '')}`.trim();
      } else if (typeof username === 'string') {
        processedUsername = normalizeText(username || '');
      }
    }

    if (currentPassword && password === currentPassword) {
      return {
        strength: 0,
        errors: ['La nueva contraseña debe ser diferente a la actual']
      };
    }

    const result = validatePassword(
      password || '',
      clientType,
      processedUsername,
      businessName
    );

    return result;
  }, [password, currentPassword, username, clientType, businessName]);

  const handleChange = useCallback(
    e => {
      if (typeof onChange === 'function') {
        onChange(e);
      }
    },
    [onChange]
  );

  const strengthInfo = useMemo(() => {
    if (!password) {
      return { level: 0, text: '', isStrong: false };
    }
    return getPasswordStrengthLevel(
      validationResult.strength,
      validationResult.errors
    );
  }, [password, validationResult]);

  const visualStrength = strengthInfo.level;

  const strengthMessage = strengthInfo.text;

  const passwordValidation = useMemo(() => {
    return {
      hasErrors: validationResult.errors && validationResult.errors.length > 0,
      isStrong: strengthInfo.isStrong,
      shouldShowTooltip:
        password &&
        ((validationResult.errors && validationResult.errors.length > 0) ||
          visualStrength >= 2)
    };
  }, [validationResult.errors, visualStrength, password, strengthInfo]);

  useEffect(() => {
    if (onErrorsChange && typeof onErrorsChange === 'function') {
      onErrorsChange(
        validationResult.errors || [],
        validationResult.strength || 0,
        passwordValidation.isStrong
      );
    }
  }, [
    onErrorsChange,
    validationResult.errors,
    validationResult.strength,
    passwordValidation.isStrong
  ]);

  const handleInputFocus = () => {
    setTooltipOpen(true);
  };

  const handleInputBlur = () => {
    setTooltipOpen(false);
  };

  return (
    <Grid container direction="column" className={classes.container}>
      {withInput && (
        <Grid item>
          <Tooltip
            title={
              <div className={classes.tooltipContainer}>
                {passwordValidation.hasErrors ? (
                  validationResult.errors.map((errorMsg, index) => (
                    <Typography key={index} className={classes.tooltipError}>
                      • {errorMsg}
                    </Typography>
                  ))
                ) : (
                  <Typography
                    className={classes.tooltipSuccess}
                    style={{
                      color: strengthColors[visualStrength] || '#ff0000'
                    }}
                  >
                    • {strengthMessage}
                  </Typography>
                )}
              </div>
            }
            open={Boolean(tooltipOpen && passwordValidation.shouldShowTooltip)}
            placement="bottom-start"
            classes={{
              tooltip: classes.customTooltip,
              popper: classes.customTooltipPopper
            }}
            arrow
          >
            <div>
              <TextInput
                inputRef={node => {
                  passwordInputRef.current = node;
                  if (typeof inputRef === 'function') {
                    inputRef(node);
                  } else if (inputRef && inputRef.hasOwnProperty('current')) {
                    inputRef.current = node;
                  }
                }}
                label={label}
                required={required}
                fullWidth
                margin="none"
                error={error}
                helperText={helperText}
                name={name}
                value={password}
                onChange={handleChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                type={passwordShown ? 'text' : 'password'}
                InputProps={{
                  autoComplete: 'off',
                  endAdornment: (
                    <ShowPassword
                      togglePasswordShow={() => setPasswordShown(prev => !prev)}
                      passwordShown={passwordShown}
                    />
                  )
                }}
              />
            </div>
          </Tooltip>
        </Grid>
      )}
      <Grid item className={classes.strengthContainer}>
        <Grid container direction="column" spacing={0}>
          <Grid item>
            <LinearProgress
              variant="determinate"
              value={(visualStrength / 4) * 100}
              className={clsx(
                classes.progressBar,
                classes[`colorBar${visualStrength}`]
              )}
            />
          </Grid>
          <Grid item>
            <Grid container alignItems="center">
              <Typography variant="caption" className={classes.strengthLabel}>
                {STRENGTH_LABELS[visualStrength]}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    width: '100%'
  },
  strengthContainer: {
    marginTop: theme.spacing(0.5)
  },
  errorContainer: {
    marginTop: theme.spacing(0.5)
  },
  errorText: {
    color: theme.palette.error.main,
    display: 'block',
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5)
  },
  progressBar: {
    height: 6,
    borderRadius: 3,
    backgroundColor: theme.palette.grey[200],
    '& .MuiLinearProgress-bar': {
      borderRadius: 3,
      transition: 'transform 0.4s ease'
    }
  },
  colorBar0: { '& .MuiLinearProgress-bar': { backgroundColor: '#808080' } },
  colorBar1: { '& .MuiLinearProgress-bar': { backgroundColor: '#ff0000' } },
  colorBar2: { '& .MuiLinearProgress-bar': { backgroundColor: '#ffa500' } },
  colorBar3: { '& .MuiLinearProgress-bar': { backgroundColor: '#90ee90' } },
  colorBar4: { '& .MuiLinearProgress-bar': { backgroundColor: '#008000' } },
  strengthLabel: {
    marginTop: theme.spacing(0.5),
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(0.5),
    position: 'relative',
    zIndex: 1
  },
  inputRoot: {
    minHeight: 58,
    '& .MuiInputBase-input': {
      height: 'inherit',
      fontSize: 14,
      caretColor: theme.palette.primary.main
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.23)'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2
    }
  },
  customTooltip: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    maxWidth: 300,
    padding: theme.spacing(1.5),
    boxShadow: theme.shadows[3],
    borderRadius: 4,
    fontSize: '0.8rem'
  },
  customTooltipPopper: {
    marginTop: '5px !important'
  },
  tooltipContainer: {
    padding: theme.spacing(0.5)
  },
  tooltipError: {
    color: theme.palette.error.main,
    fontSize: '0.8rem',
    marginBottom: theme.spacing(0.5)
  },
  tooltipSuccess: {
    color: '#008000',
    fontSize: '0.8rem'
  }
}));

export default PasswordInputStrengthMeter;
